
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type GodRaysBlurShaderUniforms = {
	uOcclusionMap: WebGLUniformLocation,
	uDepthMap: WebGLUniformLocation,
	uSunScreenPos: WebGLUniformLocation,
	uTexelSize: WebGLUniformLocation,
	uDensity: WebGLUniformLocation,
	uWeight: WebGLUniformLocation,
	uDecay: WebGLUniformLocation,
	uExposure: WebGLUniformLocation
};

export type GodRaysBlurShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type GodRaysBlurShaderSettings = string[];

export class GodRaysBlurShader extends ShaderBase<GodRaysBlurShaderUniforms, GodRaysBlurShaderAttributes> {

    private static fragmentShaderSource = `
in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uOcclusionMap;
uniform sampler2D uDepthMap;
uniform vec2 uSunScreenPos;
uniform vec2 uTexelSize;
uniform float uDensity;
uniform float uWeight;
uniform float uDecay;
uniform float uExposure;

#define NUM_SAMPLES 30

// Sample a 3x3 disk around the sun position to estimate how much of the sun
// itself is visible (sky vs. occluded by foreground geometry). Returns the
// fraction of taps that hit sky. When the sun is fully occluded (e.g. directly
// behind a model) this is 0, killing the radial blur — without it, bright sky
// elsewhere on screen smears into long streaks pointing at the occluded sun.
float sampleSunVisibility(void) {
	float vis = 0.0;
	for (int j = -1; j <= 1; j++) {
		for (int i = -1; i <= 1; i++) {
			vec2 uv = uSunScreenPos + vec2(float(i), float(j)) * uTexelSize * 2.0;
			vec3 c = texture(uOcclusionMap, clamp(uv, vec2(0.0), vec2(1.0))).rgb;
			vis += max(max(c.r, c.g), c.b) > 0.0 ? 1.0 : 0.0;
		}
	}
	return vis / 9.0;
}

void main(void) {
	float sunVis = sampleSunVisibility();
	if (sunVis <= 0.0) {
		fragColor = vec4(0.0);
		return;
	}

	vec2 deltaTexCoord = vTexCoord - uSunScreenPos;
	deltaTexCoord *= uDensity / float(NUM_SAMPLES);

	vec2 sampleCoord = vTexCoord;
	vec3 color = vec3(0.0);
	float illuminationDecay = 1.0;

	for (int i = 0; i < NUM_SAMPLES; i++) {
		sampleCoord -= deltaTexCoord;
		if (!any(lessThan(sampleCoord, vec2(0.0))) && !any(greaterThan(sampleCoord, vec2(1.0)))) {
			vec3 sampleColor = texture(uOcclusionMap, sampleCoord).rgb;
			color += sampleColor * (illuminationDecay * uWeight);
		}
		illuminationDecay *= uDecay;
		if (illuminationDecay < 0.01) break;
	}

	// Fade god rays out on foreground geometry. Without this, the radial blur
	// brightens whatever is between the camera and the sun in screen space —
	// pixels on a model that lies between camera and sun pick up streaks
	// pointing at the sun, "painting" the rays onto the model itself. Sky
	// (reversed-Z cleared depth = 0.0) and very-far geometry receive full rays;
	// mid-depth objects fade out smoothly over the far ~0.2% of NDC depth.
	//
	// Reversed-Z: depth = 0 → far plane, depth = 1 → near plane.
	// smoothstep(0.002, 0.0, depth) maps depth ≤ 0.002 (≈far field) to 1
	// (full rays) and ramps to 0 as geometry gets closer to the camera.
	float depth = texture(uDepthMap, vTexCoord).r;
	float depthFade = smoothstep(0.002, 0.0, depth);

	fragColor = vec4(color * uExposure * sunVis * depthFade, 1.0);
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: GodRaysBlurShaderUniformsContainer;
    public attributesContainer: GodRaysBlurShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: GodRaysBlurShaderSettings) {
        super(ctx, GodRaysBlurShader.fragmentShaderSource, GodRaysBlurShader.vertexShaderSource, settings);
        this.uniformsContainer = new GodRaysBlurShaderUniformsContainer(ctx, this);
        this.attributesContainer = new GodRaysBlurShaderShaderAttributesContainer(ctx, this);
    }
}

class GodRaysBlurShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: GodRaysBlurShader) {
        super(ctx);
    }

    public setOcclusionMap(texture: Texture) : GodRaysBlurShaderUniformsContainer {
        this.setTextureValue('uOcclusionMap', this.shader.uniforms.uOcclusionMap, texture, 'sampler2D');
        return this;
    }

    public setDepthMap(texture: Texture) : GodRaysBlurShaderUniformsContainer {
        this.setTextureValue('uDepthMap', this.shader.uniforms.uDepthMap, texture, 'sampler2D');
        return this;
    }

    public setSunScreenPos(value: vec2 | number[]) : GodRaysBlurShaderUniformsContainer {
        this.setValue('uSunScreenPos', this.shader.uniforms.uSunScreenPos, value, 'vec2');
        return this;
    }

    public setTexelSize(value: vec2 | number[]) : GodRaysBlurShaderUniformsContainer {
        this.setValue('uTexelSize', this.shader.uniforms.uTexelSize, value, 'vec2');
        return this;
    }

    public setDensity(value: number) : GodRaysBlurShaderUniformsContainer {
        this.setValue('uDensity', this.shader.uniforms.uDensity, value, 'float');
        return this;
    }

    public setWeight(value: number) : GodRaysBlurShaderUniformsContainer {
        this.setValue('uWeight', this.shader.uniforms.uWeight, value, 'float');
        return this;
    }

    public setDecay(value: number) : GodRaysBlurShaderUniformsContainer {
        this.setValue('uDecay', this.shader.uniforms.uDecay, value, 'float');
        return this;
    }

    public setExposure(value: number) : GodRaysBlurShaderUniformsContainer {
        this.setValue('uExposure', this.shader.uniforms.uExposure, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uOcclusionMap', uniform: this.shader.uniforms.uOcclusionMap, dataType: 'sampler2D' },
			{ name: 'uDepthMap', uniform: this.shader.uniforms.uDepthMap, dataType: 'sampler2D' }
        ];
    }
}

class GodRaysBlurShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: GodRaysBlurShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : GodRaysBlurShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : GodRaysBlurShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}